import { useNavigate } from 'react-router';
import { useToast } from '../../../hooks/use-toast';
import { ApiError } from '../../../shared.api';
import { apiErrorMessage } from '../../../policies/api-error-message';
import { me, startImpersonation, stopImpersonation } from '../session.api';
import { useCurrentUser } from '../components/CurrentUserProvider';

/** The server's reason for refusing (e.g. "Sign in with a code, or turn on two-factor sign-in, before acting as another user"), as a sentence. */
function refusalReason(err: unknown): string | undefined {
  const message = apiErrorMessage(err);
  return message ? `${message}.` : undefined;
}

/**
 * Start and stop acting as another user. Both land on `/`: the previous identity's pages and
 * their loaded data must not linger under the new one. Row-level actions with no form, so
 * failures toast (CLAUDE.md → Web conventions).
 */
export function useImpersonation() {
  const navigate = useNavigate();
  const { setUser } = useCurrentUser();
  const toast = useToast();

  async function start(target: { id: string; name: string }): Promise<void> {
    try {
      setUser(await startImpersonation({ userId: target.id }));
      navigate('/');
      toast.success(`Now acting as ${target.name}`);
    } catch (err) {
      toast.error(refusalReason(err) ?? `Couldn't start acting as ${target.name}`);
    }
  }

  async function stop(): Promise<void> {
    try {
      const admin = await stopImpersonation();
      setUser(admin);
      navigate('/');
      toast.success(`Stopped acting as another user — you're ${admin.name} again`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) return syncWithServer();
      toast.error("Couldn't stop acting as this user");
    }
  }

  /** A 409 on stop means the server already ended the impersonation (e.g. the target was deactivated), so the banner is stale: show who the server says we are. */
  async function syncWithServer(): Promise<void> {
    try {
      setUser(await me());
      navigate('/');
      toast.info('You are no longer acting as another user');
    } catch {
      toast.error("Couldn't stop acting as this user");
    }
  }

  return { start, stop };
}
